import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, IconButton, styled } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Controller, useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { normalizeFileName } from "../../../../../../common/utils/utils";
import { FornecedorDocumento } from "../../../../domain/models/fornecedor_documento";
import { zodResolver } from "@hookform/resolvers/zod";
import { FornecedorDocumentoForm, FornecedorDocumentoSchema } from "../../../schemas/Fornecedor_cadastro/fornecedor_documento";
import { useCallback, useEffect } from "react";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import dayjs from "dayjs";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * FornecedorDocumentoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedDocumento: FornecedorDocumento | null;
  onClose: () => void;
  onSubmit: (form: FornecedorDocumentoForm) => void;
  onDownloadFile: (idFornecedorArquivo: number, nomeArquivo: string) => void;
};

export const FornecedorDocumentoModal = ({
  open,
  saving,
  loading,
  selectedDocumento,
  onClose,
  onSubmit,
  onDownloadFile,
}: Props) => {
  const hookForm = useForm<FornecedorDocumentoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(FornecedorDocumentoSchema),
  });

  const [file, limparArquivo] = hookForm.watch(['file', 'limparArquivo']);

  useEffect(() => {
    if (open) {
      if (selectedDocumento) {
        hookForm.reset();

        hookForm.setValue('status', selectedDocumento.status ?? 1);
        hookForm.setValue('nomeDocumento', selectedDocumento.descricao ?? '');
        hookForm.setValue('limparArquivo', false);
        hookForm.setValue('possuiArquivo', selectedDocumento.nome !== null &&
          selectedDocumento.nome !== undefined && selectedDocumento.nome.length > 0);

        if (selectedDocumento.dataVencimento && selectedDocumento.dataVencimento.length > 0) {
          hookForm.setValue('dataVencimento', dayjs(selectedDocumento.dataVencimento).toDate());
        }
      } else {
        hookForm.reset({
          status: 1,
          limparArquivo: false,
          possuiArquivo: false,
        });
      }
    }
  }, [
    open,
    selectedDocumento,
    hookForm,
  ]);

  const handleDeleteFile = useCallback(() => {
    if (!saving) {
      if (window.confirm('Você deseja excluir este item?')) {
        hookForm.setValue('limparArquivo', true);
        hookForm.setValue('possuiArquivo', false);
      }
    }
  }, [
    saving,
    hookForm,
  ]);

  const handleDownloadFile = useCallback(() => {
    if (!saving && selectedDocumento) onDownloadFile(selectedDocumento.idFornecedorArquivo, selectedDocumento.nome ?? '');
  }, [
    saving,
    selectedDocumento,
    onDownloadFile,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title="Documento"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className="w-full flex flex-col gap-4">
        <Controller
          name='status'
          control={hookForm.control}
          render={({ field }) => (
            <FormControl size="small" className="grid-col-1" required={true}>
              <FormLabel id="statusArquivo" required={true}>
                Status do Arquivo
              </FormLabel>
              <RadioGroup
                aria-labelledby="statusArquivo"
                name="statusArquivo"
                row={true}
                value={field.value ?? null}
                onChange={(e) => { if (!saving) field.onChange(e); }}>

                <FormControlLabel
                  className="text-black dark:text-white"
                  value={1}
                  control={<Radio />}
                  label="Atual"
                />

                <FormControlLabel
                  className="text-black dark:text-white"
                  value={2}
                  control={<Radio />}
                  label="Obsoleto"
                />

              </RadioGroup>
            </FormControl>
          )} />


        <DefaultFormInput
          name='nomeDocumento'
          label='Nome do Documento'
          readOnly={saving}
          required={true}
          reactHookForm={hookForm}
        />

        <Controller
          name='dataVencimento'
          control={hookForm.control}
          render={({ field }) => (
            <DatePicker
              readOnly={saving}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
              label="Data de Vencimento"
              value={field.value ? dayjs.utc(field.value as any).local() : null}
              onChange={e => field.onChange(e && dayjs(e).isValid() ? dayjs.utc(e.toISOString()) : null)} />
          )} />

        <div className="flex w-full">
          <Controller
            name='file'
            control={hookForm.control}
            render={({ field }) => {
              return (
                <div className="w-full">
                  {
                    ((file === undefined || file === null) && !limparArquivo &&
                      selectedDocumento && selectedDocumento.nome && selectedDocumento.nome.length > 0) ? (
                      <div className="w-full flex flex-row justify-between items-center">
                        <span
                          className="cursor-pointer"
                          onMouseDown={() => handleDownloadFile()}>
                          {selectedDocumento.nome && selectedDocumento.nome.length > 95
                            ? `${selectedDocumento.nome.substring(0, 95)}...`
                            : `${selectedDocumento.nome}`}
                        </span>
                        <IconButton onClick={() => handleDeleteFile()}>
                          <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                        </IconButton>
                      </div>
                    ) : (
                      <PrimaryButton
                        component="label"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        disabled={saving}
                        startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                        {field.value ? normalizeFileName(field.value?.name, 40) : "Escolher arquivo"}
                        <VisuallyHiddenInput
                          type="file"
                          onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              hookForm.setValue('file', e.target.files[0]);
                              hookForm.setValue('limparArquivo', false);
                              hookForm.setValue('possuiArquivo', true);
                            }
                          }}
                          multiple={false}
                        />
                      </PrimaryButton>
                    )
                  }
                  <div className="h-[1.5rem] text-sm text-red-500">
                    {hookForm.formState.errors.file !== undefined && hookForm.formState.errors.file.message}
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
    </BaseFormControlledModal>
  );
}